var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ErrorInvalidReturnedStateParam, ErrorNoAccessToken, ErrorNoAuthCode, toErrorObject } from './errors';
import { extractParamFromUrl, generatePKCECodeChallengeAndVerifier, generateRandomState, objectToQueryString, parseWwwAuthenticateHeader } from './helpers';
export * from './errors';
const HEADER_AUTHORIZATION = 'Authorization';
const HEADER_WWW_AUTHENTICATE = 'WWW-Authenticate';
export const RECOMMENDED_STATE_LENGTH = 32;
export class OAuth2AuthCodePkceClient {
    constructor(config, storage) {
        this.state = {};
        this.config = config;
        this.storage = storage || LocalStorage;
        this.ready = new Promise(resolve => this.setReady = resolve);
        this.recoverState();
    }
    reset() {
        return __awaiter(this, void 0, void 0, function* () {
            this.state = {};
            yield this.saveState();
            this.authCodeForAccessTokenPromise = undefined;
            this.refreshTokenForAccessTokenPromise = undefined;
        });
    }
    requestAuthorizationCode(oneTimeParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientId, extraAuthorizationParams, redirectUrl, scopes } = this.config;
            const { codeChallenge, codeVerifier } = yield generatePKCECodeChallengeAndVerifier();
            const stateQueryParam = generateRandomState(RECOMMENDED_STATE_LENGTH);
            this.state = Object.assign(Object.assign({}, this.state), { codeChallenge,
                codeVerifier,
                stateQueryParam });
            this.saveState();
            let url = this.config.authorizationUrl
                + '?response_type=code&'
                + `client_id=${encodeURIComponent(clientId)}&`
                + `redirect_uri=${encodeURIComponent(redirectUrl)}&`
                + `state=${stateQueryParam}&`
                + `code_challenge=${encodeURIComponent(codeChallenge)}&`
                + 'code_challenge_method=S256';
            if (scopes) {
                url += `&scope=${encodeURIComponent(scopes.join(' '))}`;
            }
            if (extraAuthorizationParams || oneTimeParams) {
                const extraParameters = Object.assign(Object.assign({}, extraAuthorizationParams), oneTimeParams);
                url += `&${objectToQueryString(extraParameters)}`;
            }
            location.replace(url);
        });
    }
    isReturningFromAuthServer() {
        return !!extractParamFromUrl('code', location.href);
    }
    receiveCode() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.ready;
            const error = extractParamFromUrl('error', location.href);
            if (error) {
                throw toErrorObject(error);
            }
            const stateQueryParam = extractParamFromUrl('state', location.href);
            if (stateQueryParam !== this.state.stateQueryParam) {
                console.warn('"state" parameter doesn\'t match the one sent! ' +
                    'Possible malicious activity.');
                throw new ErrorInvalidReturnedStateParam();
            }
            this.state.authorizationCode = extractParamFromUrl('code', location.href);
            if (!this.state.authorizationCode) {
                throw new ErrorNoAuthCode();
            }
            this.saveState();
        });
    }
    getAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getTokens();
        });
    }
    getTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const { accessToken, authorizationCode, idToken, refreshToken, scopes } = this.state;
            if (authorizationCode) {
                return this.exchangeAuthCodeForAccessToken();
            }
            if (!accessToken) {
                throw new ErrorNoAccessToken();
            }
            if (this.isAccessTokenExpired()) {
                if (this.config.onAccessTokenExpiry) {
                    return this.config.onAccessTokenExpiry();
                }
            }
            return Promise.resolve({ accessToken, idToken, refreshToken, scopes });
        });
    }
    exchangeAuthCodeForAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.authCodeForAccessTokenPromise) {
                this.authCodeForAccessTokenPromise = this.fetchAccessTokenUsingCode();
            }
            const tokenResponse = yield this.authCodeForAccessTokenPromise;
            this.authCodeForAccessTokenPromise = undefined;
            this.state.authorizationCode = undefined;
            return this.setTokens(tokenResponse);
        });
    }
    exchangeRefreshTokenForAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.refreshTokenForAccessTokenPromise) {
                this.refreshTokenForAccessTokenPromise = this.fetchAccessTokenUsingRefreshToken();
            }
            const tokenResponse = yield this.refreshTokenForAccessTokenPromise;
            this.refreshTokenForAccessTokenPromise = undefined;
            return this.setTokens(tokenResponse);
        });
    }
    makeRetryFetchFunction(fetchFunc) {
        return (input, ...rest) => __awaiter(this, void 0, void 0, function* () {
            const response = yield fetchFunc(input, ...rest);
            if (response.status === 401) {
                const authenticateHeader = response.headers.get(HEADER_WWW_AUTHENTICATE.toLowerCase());
                if (authenticateHeader) {
                    const error = parseWwwAuthenticateHeader(authenticateHeader).error;
                    if (error === 'invalid_token') {
                        yield this.exchangeRefreshTokenForAccessToken();
                        input = yield this.requestInterceptor(input);
                        return fetchFunc(input, ...rest);
                    }
                }
            }
            return response;
        });
    }
    decorateFetchWithInterceptors(fetchFunc) {
        return (input, ...rest) => __awaiter(this, void 0, void 0, function* () {
            if (typeof input === 'string') {
                input = new Request(input);
            }
            input = yield this.requestInterceptor(input);
            const response = yield fetchFunc(input, ...rest);
            return this.responseInterceptor(response);
        });
    }
    requestInterceptor(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenContext = yield this.getTokens();
            request.headers.set(HEADER_AUTHORIZATION, `Bearer ${tokenContext.accessToken}`);
            return request;
        });
    }
    responseInterceptor(response) {
        return __awaiter(this, void 0, void 0, function* () {
            if (response.status !== 401) {
                return response;
            }
            const authenticateHeader = response.headers.get(HEADER_WWW_AUTHENTICATE.toLowerCase());
            if (authenticateHeader) {
                const error = parseWwwAuthenticateHeader(authenticateHeader).error;
                if (error === 'invalid_grant' && this.config.onInvalidGrant) {
                    yield this.config.onInvalidGrant();
                }
                if (error === 'invalid_token' && this.config.onInvalidToken) {
                    yield this.config.onInvalidToken();
                }
                throw toErrorObject(error);
            }
            return response;
        });
    }
    getGrantedScopes() {
        return this.state.scopes;
    }
    isAuthorized() {
        return !!this.state.accessToken;
    }
    isAccessTokenExpired() {
        const { accessTokenExpiry } = this.state;
        return Boolean(accessTokenExpiry && (new Date()) >= (new Date(accessTokenExpiry)));
    }
    fetchAccessTokenUsingCode() {
        return __awaiter(this, void 0, void 0, function* () {
            const { authorizationCode, codeVerifier = '' } = this.state;
            const { clientId, redirectUrl } = this.config;
            if (!codeVerifier) {
                console.warn('No code verifier is being sent.');
            }
            else if (!authorizationCode) {
                console.warn('No authorization grant code is being passed.');
            }
            const url = this.config.tokenUrl;
            const body = 'grant_type=authorization_code&'
                + `code=${encodeURIComponent(authorizationCode || '')}&`
                + `redirect_uri=${encodeURIComponent(redirectUrl)}&`
                + `client_id=${encodeURIComponent(clientId)}&`
                + `code_verifier=${codeVerifier}`;
            return this.makeTokenRequest(url, body);
        });
    }
    fetchAccessTokenUsingRefreshToken() {
        const { extraRefreshParams, clientId, tokenUrl } = this.config;
        const { refreshToken } = this.state;
        if (!refreshToken) {
            console.warn('No refresh token is present.');
        }
        const url = tokenUrl;
        let body = 'grant_type=refresh_token&'
            + `refresh_token=${refreshToken}&`
            + `client_id=${clientId}`;
        if (extraRefreshParams) {
            body = `${url}&${objectToQueryString(extraRefreshParams)}`;
        }
        return this.makeTokenRequest(url, body);
    }
    makeTokenRequest(url, body) {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenResponse = yield fetch(url, {
                method: 'POST',
                body,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            const jsonContent = yield tokenResponse.json();
            if (!tokenResponse.ok) {
                if (jsonContent.error === 'invalid_grant' && this.config.onInvalidGrant) {
                    yield this.config.onInvalidGrant();
                }
                throw toErrorObject(jsonContent.error);
            }
            const { access_token, expires_in, id_token, refresh_token, scope } = jsonContent;
            return {
                accessToken: access_token,
                expiresIn: expires_in,
                idToken: id_token,
                refreshToken: refresh_token,
                scope
            };
        });
    }
    setTokens(tokenResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            const { accessToken, expiresIn, idToken, refreshToken, scope } = tokenResponse;
            this.state.accessToken = accessToken;
            this.state.accessTokenExpiry = (new Date(Date.now() + (parseInt(expiresIn, 10) * 1000)))
                .toString();
            if (idToken) {
                this.state.idToken = idToken;
            }
            if (refreshToken) {
                this.state.refreshToken = refreshToken;
            }
            if (scope) {
                this.state.scopes = scope.split(' ');
            }
            yield this.saveState();
            return {
                accessToken: this.state.accessToken,
                idToken: this.state.idToken,
                refreshToken: this.state.refreshToken,
                scopes: scope ? this.state.scopes : []
            };
        });
    }
    recoverState() {
        return __awaiter(this, void 0, void 0, function* () {
            this.state = JSON.parse((yield this.storage.loadState()) || '{}');
            this.setReady();
            if (!this.config.storeRefreshToken) {
                this.state.refreshToken = this.refreshToken;
            }
        });
    }
    saveState() {
        return __awaiter(this, void 0, void 0, function* () {
            this.refreshToken = this.state.refreshToken;
            const state = Object.assign({}, this.state);
            if (!this.config.storeRefreshToken) {
                delete state.refreshToken;
            }
            yield this.storage.saveState(JSON.stringify(state));
        });
    }
}
const LOCALSTORAGE_STATE = 'oauth2authcodepkce-state';
const LocalStorage = {
    saveState: (serializedState) => localStorage.setItem(LOCALSTORAGE_STATE, serializedState),
    loadState: () => localStorage.getItem(LOCALSTORAGE_STATE)
};
